import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from 'src/utils/toast.service';

@Component({
  selector: 'upload-file',
  imports: [CommonModule],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.scss'
})
export class UploadFileComponent {
  @Output() fileSelected = new EventEmitter<File>();
  @Input() validFormat: string | null = null;
  @Input() validFormatList: string[] | null = null;
  @Input() maxFileSizeMB: number = 300;
  @Input() isLoading: boolean = false;

  public toastService = inject(ToastService);
  isDragOver = false;

  onFileChange(event: Event) {
    if (this.isLoading) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.handleFile(input.files[0]);
    }
  }

  onDrop(event: DragEvent) {
    if (this.isLoading) return;
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    if (this.isLoading) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    if (this.isLoading) return;
    event.preventDefault();
    this.isDragOver = false;
  }

  handleFile(file: File) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const maxSize = this.maxFileSizeMB * 1024 * 1024;
    let isValidFormat = true;

    if (this.validFormatList && this.validFormatList.length > 0) {
      isValidFormat = this.validFormatList.some(format => format.toLowerCase() === ext);
    } else if (this.validFormat) {
      isValidFormat = ext === this.validFormat.toLowerCase();
    }

    if (!isValidFormat) {
      const excelExts = ['xlsx', 'xls'];
      const buildFormatList = (formats: string[]): string => {
        const excelMatches = formats.filter(f => excelExts.includes(f.toLowerCase()));
        const others = formats.filter(f => !excelExts.includes(f.toLowerCase()));
        const parts: string[] = [];
        if (excelMatches.length) parts.push(`Excel (${excelMatches.map(f => `.${f.toLowerCase()}`).join(', ')})`);
        others.forEach(f => parts.push(f.toUpperCase()));
        return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} o ${parts[parts.length - 1]}` : parts[0] ?? '';
      };
      const allowedFormats = this.validFormatList?.length
        ? buildFormatList(this.validFormatList)
        : this.validFormat ? this.validFormat.toUpperCase() : '';
      const formatMsg = allowedFormats ? ` Solo se permiten archivos ${allowedFormats}.` : '';
      this.toastService.showToast(
        'fi fi-sr-triangle-warning text-[24px] text-negative',
        `Formato no válido.${formatMsg}`,
        'error'
      );
      return;
    }

    if (file.size > maxSize) {
      this.toastService.showToast(
        'fi fi-sr-triangle-warning text-[24px] text-negative',
        `El tamaño del archivo excede el máximo permitido (${this.maxFileSizeMB} MB).`,
        'error'
      );
      return;
    }
    this.fileSelected.emit(file);
  }

  getValidFormatsText(): string {
    if (this.validFormatList && this.validFormatList.length > 0) {
      return this.validFormatList.join(', ');
    } else if (this.validFormat) {
      return this.validFormat;
    } else {
      return 'Cualquiera';
    }
  }

  getAcceptAttribute(): string | null {
    if (this.validFormatList && this.validFormatList.length > 0) {
      return this.validFormatList.map(f => '.' + f.toLowerCase()).join(',');
    }
    if (this.validFormat) {
      return '.' + this.validFormat.toLowerCase();
    }
    return null;
  }
}
